import { ApiError, getPlan, negotiateRealtime } from '../api/client';
import type { PlanResponse, RealtimeConnectionResponse, RealtimeNotice } from '../api/types';

// The ADR-028 §6 subscription, as a plain controller so every branch is testable without
// React: negotiate → open a native WebSocket (no subprotocol, no library) → on a
// `plan-changed` notice with a version ahead of ours, refetch the document once (notices that
// arrive during a refetch collapse into a single trailing one) → hand the newer document
// upstairs; on a `plan-renamed` notice (§3 as amended 2026-09-16), hand the name it carries
// upstairs with no refetch at all — the name lives outside the versioned document, and every
// refetch path resolves it through the backend's per-instance node cache, whose eviction rides
// another connection with no ordering against this socket, so a re-read could answer the old
// name; the notice's `updatedAt` is the monotonic guard against reordered notices. Reconnects
// with capped exponential backoff, rotates the credential shortly before it expires (Web PubSub
// keeps an open socket past `exp`; the rotation is what bounds revocation lag to the token TTL),
// and stops silently where realtime is unavailable. The resume path stays refetch + rotate: the
// name is deliberately never taken from a refetch (unstamped, cache-served — it could regress
// a live name).

/** The subset of WebSocket the controller uses — injectable for tests. */
export interface SocketLike {
  addEventListener(type: 'open' | 'message' | 'close' | 'error', listener: (event: any) => void): void;
  close(): void;
}

export interface SubscriptionDeps {
  negotiate: (planId: string) => Promise<RealtimeConnectionResponse>;
  fetchPlan: (planId: string) => Promise<PlanResponse>;
  connect: (url: string) => SocketLike;
  now: () => number;
  setTimer: (callback: () => void, ms: number) => unknown;
  clearTimer: (handle: unknown) => void;
  /**
   * The host coming back to the foreground (ADR-029 §5 — the native shells: iOS suspends the
   * socket within seconds of backgrounding): the controller refetches once and rotates the
   * socket. Registers the callback, returns the unregister. Absent on the web.
   */
  resume?: (callback: () => void) => () => void;
}

/** What the controller hands upstairs. */
export interface SubscriptionHandlers {
  /** A fetched document whose version was ahead of the client's at fetch time. */
  onPlan: (plan: PlanResponse) => void;
  /**
   * A collaborator's rename as its `plan-renamed` notice carried it — the stored name, handed
   * up with no refetch because the header path (`X-Resource-Name`) runs through the backend's
   * per-instance node cache; only with an `updatedAt` not older than the last adopted.
   */
  onRenamed: (planId: string, name: string) => void;
}

export const defaultDeps: SubscriptionDeps = {
  negotiate: negotiateRealtime,
  fetchPlan: getPlan,
  connect: (url) => new WebSocket(url),
  now: () => Date.now(),
  setTimer: (callback, ms) => setTimeout(callback, ms),
  clearTimer: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
};

/** Backoff schedule for reconnects: 1 s doubling to a 30 s cap. */
export const INITIAL_BACKOFF_MS = 1_000;
export const MAX_BACKOFF_MS = 30_000;
/** How long before the credential expires the controller rotates (re-negotiates + reconnects). */
export const ROTATION_MARGIN_MS = 30_000;
/** The rotation never fires sooner than this after connecting (a very short TTL still rotates). */
export const MIN_ROTATION_MS = 5_000;

/**
 * Subscribes to `planId` and returns the function that stops the subscription.
 * `currentVersion` is read at every decision (never captured); `handlers.onPlan` receives a
 * fetched document whose version was ahead of the client's at fetch time, `handlers.onRenamed`
 * the name a collaborator's rename notice carried.
 */
export function subscribe(
  planId: string,
  currentVersion: () => number,
  handlers: SubscriptionHandlers,
  deps: SubscriptionDeps = defaultDeps,
): () => void {
  let stopped = false;
  let socket: SocketLike | null = null;
  let timer: unknown = null;
  let backoff = INITIAL_BACKOFF_MS;
  let refetching = false;
  let refetchPending = false;
  // The `updatedAt` of the last rename adopted — the floor a later notice must not fall below.
  // Per subscription, so it survives a socket rotation.
  let renamedAt = Number.NEGATIVE_INFINITY;
  // Incremented for every socket generation so a stale socket's events are ignored.
  let generation = 0;

  const clearTimer = () => {
    if (timer !== null) {
      deps.clearTimer(timer);
      timer = null;
    }
  };

  const schedule = (callback: () => void, ms: number) => {
    clearTimer();
    timer = deps.setTimer(() => {
      timer = null;
      callback();
    }, ms);
  };

  const closeSocket = () => {
    if (socket !== null) {
      const closing = socket;
      socket = null;
      generation += 1;
      closing.close();
    }
  };

  const retryLater = () => {
    if (stopped) return;
    schedule(start, backoff);
    backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
  };

  const refetch = () => {
    if (refetching) {
      refetchPending = true;
      return;
    }
    refetching = true;
    refetchPending = false;
    deps
      .fetchPlan(planId)
      .then((plan) => {
        if (!stopped && plan.version > currentVersion()) handlers.onPlan(plan);
      })
      .catch(() => {
        // A failed refetch is repaired by the next notice or a reload — nothing to surface.
      })
      .finally(() => {
        refetching = false;
        if (refetchPending && !stopped) refetch();
      });
  };

  const onMessage = (data: unknown) => {
    if (typeof data !== 'string') return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(data);
    } catch {
      return;
    }
    if (typeof parsed !== 'object' || parsed === null) return;
    const notice = parsed as RealtimeNotice;
    if (notice.planId !== planId) return;
    switch (notice.type) {
      case 'plan-changed':
        if (notice.version > currentVersion()) refetch();
        return;
      case 'plan-renamed': {
        // Trimmed and non-empty is all the client asks; the length cap is the server's
        // invariant (the same posture as `resourceNameOf` in api/client.ts).
        const name = typeof notice.name === 'string' ? notice.name.trim() : '';
        if (name === '') return;
        const stamp = typeof notice.updatedAt === 'string' ? Date.parse(notice.updatedAt) : Number.NaN;
        // Strictly older than the last adopted (reordered) → drop. NaN never compares lower →
        // adopt, last wins, never a silent death; equal → adopt, idempotent downstairs.
        if (stamp < renamedAt) return;
        if (!Number.isNaN(stamp)) renamedAt = stamp;
        handlers.onRenamed(planId, name);
        return;
      }
      default:
        return;
    }
  };

  const open = (connection: RealtimeConnectionResponse) => {
    const mine = generation + 1;
    generation = mine;
    const ws = deps.connect(connection.url);
    socket = ws;
    ws.addEventListener('open', () => {
      if (generation !== mine) return;
      backoff = INITIAL_BACKOFF_MS;
      const untilExpiry = Date.parse(connection.expiresAt) - deps.now();
      schedule(rotate, Math.max(untilExpiry - ROTATION_MARGIN_MS, MIN_ROTATION_MS));
    });
    ws.addEventListener('message', (event: { data: unknown }) => {
      if (generation === mine) onMessage(event.data);
    });
    ws.addEventListener('close', () => {
      if (generation !== mine || stopped) return;
      socket = null;
      retryLater();
    });
    ws.addEventListener('error', () => {
      // The close event follows an error; reconnecting is decided there.
    });
  };

  const rotate = () => {
    closeSocket();
    start();
  };

  // Foregrounded: the document may have moved while suspended and the socket may be dead.
  const stopResume =
    deps.resume?.(() => {
      if (stopped) return;
      refetch();
      rotate();
    }) ?? null;

  function start() {
    deps
      .negotiate(planId)
      .then((connection) => {
        if (!stopped) open(connection);
      })
      .catch((error: unknown) => {
        if (error instanceof ApiError && error.body.code === 'Realtime.Unavailable') {
          // No realtime here (local runs): stay silently non-live.
          return;
        }
        retryLater();
      });
  }

  start();

  return () => {
    stopped = true;
    clearTimer();
    closeSocket();
    stopResume?.();
  };
}
